// src/utils/dreamTeamUtils.js
// Shared dream team selection logic used by DreamTeam.js and YourPoints.js.
// No Firestore calls — pure calculation from already-loaded data.

/**
 * Selects the dream team from a list of players and a points map.
 *
 * @param {Array}  players    - Array of player objects with { id, name, position }
 * @param {Object} pointsMap  - { playerID: basePoints (number) }
 * @param {Object} squadConfig - From leagueConfig.squadConfig:
 *                               { positions, positionRules, maxPlayers }
 * @returns {{ [position]: string[] }} - e.g. { DEF: [id,...], MID: [...], FWD: [...] }
 */
export function selectDreamTeam(players, pointsMap, squadConfig) {
  const positions = squadConfig.positions || ["DEF", "MID", "FWD"];
  const positionRules = squadConfig.positionRules || {};
  const totalSlots = squadConfig.maxPlayers || 4;

  // Sort each position's players by pts desc, then alphabetical tiebreaker
  const byPosition = {};
  positions.forEach((pos) => {
    byPosition[pos] = players
      .filter((p) => p.position === pos)
      .map((p) => ({ id: p.id, name: p.name || p.id, pts: pointsMap[p.id] || 0 }))
      .sort((a, b) =>
        b.pts !== a.pts ? b.pts - a.pts : (a.name || "").localeCompare(b.name || "")
      );
  });

  // Step 1: fill minimums per position
  const selected = {};
  const usedIDs = new Set();
  positions.forEach((pos) => {
    selected[pos] = [];
    const minN = positionRules[pos]?.min || 0;
    (byPosition[pos] || []).slice(0, minN).forEach((p) => {
      selected[pos].push(p);
      usedIDs.add(p.id);
    });
  });

  // Step 2: collect flex candidates (not yet picked, within position max)
  const flexCandidates = [];
  positions.forEach((pos) => {
    const maxN = positionRules[pos]?.max ?? totalSlots;
    const alreadyPicked = selected[pos].length;
    const canAdd = maxN - alreadyPicked;
    (byPosition[pos] || [])
      .filter((p) => !usedIDs.has(p.id))
      .slice(0, canAdd)
      .forEach((p) => flexCandidates.push({ ...p, _pos: pos }));
  });

  // Sort flex candidates and fill remaining slots
  flexCandidates.sort((a, b) =>
    b.pts !== a.pts ? b.pts - a.pts : (a.name || "").localeCompare(b.name || "")
  );
  const filled = positions.reduce((s, pos) => s + selected[pos].length, 0);
  const remaining = totalSlots - filled;
  flexCandidates.slice(0, remaining).forEach((cand) => {
    selected[cand._pos].push(cand);
    usedIDs.add(cand.id);
  });

  // Return { pos: [id, ...] }
  const team = {};
  positions.forEach((pos) => {
    team[pos] = selected[pos].map((p) => p.id);
  });
  return team;
}
